import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { GameBoard } from '../components/GameBoard';
import { Keyboard } from '../components/Keyboard';
import { GameStats } from '../components/GameStats';
import { GuessDistribution } from '../components/GuessDistribution';
import { ShareButton } from '../components/ShareButton';
import { useFiardleGame } from '../hooks/useFiardleGame';
import { useFiardleStats } from '../hooks/useFiardleStats';

export function FiardleGamePage() {
  const game = useFiardleGame();
  const { stats, refetch: refetchStats } = useFiardleStats();

  const isComplete = game.status === 'won' || game.status === 'lost';

  const handleNewGame = async () => {
    await game.startNewGame();
    refetchStats();
  };

  if (game.loading) {
    return (
      <div className="flex justify-center items-center h-64" data-testid="loading">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (game.status === 'idle') {
    return (
      <div className="flex flex-col items-center gap-6 pt-12">
        <h1 className="text-4xl font-bold">Fiardle</h1>
        <p className="text-muted-foreground">Guess the 5-letter word in 6 tries</p>
        <Button onClick={handleNewGame} size="lg" data-testid="new-game-button">
          New Game
        </Button>
        {game.error && (
          <div className="text-red-500 text-sm" data-testid="error-message">
            {game.error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 py-4 max-w-lg mx-auto">
      <h1 className="text-2xl font-bold">Fiardle</h1>

      {game.error && (
        <div className="text-red-500 text-sm" data-testid="error-message">
          {game.error}
        </div>
      )}

      <GameBoard guesses={game.guesses} currentGuess={game.currentGuess} />

      {!isComplete && (
        <Keyboard
          letterStates={game.letterStates}
          onKeyPress={game.onKeyPress}
          onEnter={game.onEnter}
          onBackspace={game.onBackspace}
          disabled={game.submitting}
        />
      )}

      {isComplete && (
        <Card className="w-full">
          <CardHeader>
            <CardTitle className="text-center">
              {game.status === 'won' ? 'You won!' : 'Game Over'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {game.answer && (
              <p className="text-center text-muted-foreground">
                The word was <span className="font-bold uppercase">{game.answer}</span>
              </p>
            )}

            {stats && (
              <>
                <GameStats
                  gamesPlayed={stats.gamesPlayed}
                  gamesWon={stats.gamesWon}
                  currentStreak={stats.currentStreak}
                  maxStreak={stats.maxStreak}
                  winRate={stats.winRate}
                />
                <GuessDistribution distribution={stats.guessDistribution} />
              </>
            )}

            <div className="flex justify-center gap-2">
              <ShareButton guesses={game.guesses} gameStatus={game.status as 'won' | 'lost'} />
              <Button onClick={handleNewGame} variant="outline" data-testid="play-again-button">
                Play Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
